/**
 * CheckinTaskList Component
 *
 * Exibe a lista de tarefas executadas durante um check-in ativo.
 * Mostra o progresso da visita em tempo real.
 *
 * @since 2026-01-29
 * @module tmf_checkin
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { checkinApi } from '@/services/checkinApi';
import type { CheckinTask } from '@/types/checkin';

interface CheckinTaskListProps {
  checkinId: number;
  compact?: boolean;
}

export function CheckinTaskList({ checkinId, compact = false }: CheckinTaskListProps) {
  const { t } = useTranslation('checkin');

  const { data, isLoading, error } = useQuery({
    queryKey: ['checkin-tasks', checkinId],
    queryFn: () => checkinApi.getTasks(checkinId),
    refetchInterval: 30000, // Atualiza a cada 30s
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 bg-red-50 dark:bg-red-500/10 rounded-lg">
        <p className="text-sm text-red-600 dark:text-red-400">
          {t('tasks.error', 'Erro ao carregar tarefas')}
        </p>
      </div>
    );
  }

  const tasks = data?.tasks || [];

  if (tasks.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-2">
          <i className="bi bi-clipboard-check text-muted-foreground text-xl" />
        </div>
        <p className="text-sm text-muted-foreground">
          {t('tasks.empty', 'Nenhuma tarefa executada ainda')}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {t('tasks.emptyHint', 'As tarefas criadas durante a visita aparecem aqui')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-foreground">
          {t('tasks.title', 'Tarefas da Visita')}
        </h4>
        <span className="text-xs px-2 py-0.5 bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 rounded-full">
          {tasks.length}
        </span>
      </div>

      {/* Task list */}
      <div className={compact ? 'max-h-40 overflow-y-auto' : 'space-y-2'}>
        {tasks.map((task) => (
          <TaskItem key={task.id} task={task} compact={compact} />
        ))}
      </div>
    </div>
  );
}

interface TaskItemProps {
  task: CheckinTask;
  compact?: boolean;
}

function TaskItem({ task, compact }: TaskItemProps) {
  const formatTime = (dateStr?: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 py-1.5 border-b border-border last:border-0">
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: task.status_color || '#9ca3af' }}
        />
        <span className="text-sm text-muted-foreground truncate flex-1">
          {task.title || task.resource_name || 'Tarefa'}
        </span>
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {formatTime(task.datetime_created)}
        </span>
      </div>
    );
  }

  return (
    <div className="p-3 bg-muted rounded-lg">
      <div className="flex items-start gap-3">
        <div
          className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
          style={{ backgroundColor: task.status_color || '#9ca3af' }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h5 className="text-sm font-medium text-foreground truncate">
              {task.title || task.resource_name || 'Tarefa'}
            </h5>
            {task.status_name && (
              <span
                className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: `${task.status_color || '#9ca3af'}20`,
                  color: task.status_color || '#6b7280',
                }}
              >
                {task.status_name}
              </span>
            )}
          </div>
          {task.description && (
            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
              {task.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
            {task.resource_name && (
              <span className="flex items-center gap-1">
                <i className="bi bi-folder" />
                {task.resource_name}
              </span>
            )}
            {task.datetime_created && (
              <span className="flex items-center gap-1">
                <i className="bi bi-clock" />
                {formatTime(task.datetime_created)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckinTaskList;
